import { Flex, Button, useDisclosure } from '@chakra-ui/react';
import { getIn } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { CreditAgreement, Date, Util } from '@finley-technologies/finley-types';

import { LenderPostgresPreviewDrawer } from './PreviewPostgresQueryDrawer';
import { PostgresQueryResultWithWarningsAndInputsGQLType } from '../../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { colors } from 'packages/common-frontend/colors';
import { useEvaluatePortfolioCovenantPostgresQueryLazyQueryGQLType } from 'packages/creditAgreements/requests/requests.gql.generated';
import { WipConfig } from 'packages/creditAgreements/ui/types';

type PreviewPostgresQueryButtonBaseProps = {
  wipConfig: WipConfig;
  postgresQuery: CreditAgreement.Config.PortfolioCovenant.PostgresQuery.Query;
  inputDateType: 'reportingDate' | 'triggerDate';
  selectedInputDate?: Util.FullDateStr;
  presetVariables: CreditAgreement.Config.QueryVariable[];
  queryType: CreditAgreement.Config.PortfolioCovenant.PostgresQuery.QueryType;
};

interface PreviewPostgresQueryButtonPrincipalBalanceDailyAverageProps
  extends PreviewPostgresQueryButtonBaseProps {
  queryType: 'principalBalanceDailyAverage';
  postgresQuery: CreditAgreement.Config.CommonPostgresQuery.Query.PrincipalBalanceDailyAverageQuery;
  startDate?: Util.FullDateStr;
  endDate?: Util.FullDateStr;
}

interface PreviewPostgresQueryButtonTAccountsSumProps
  extends PreviewPostgresQueryButtonBaseProps {
  queryType: 'tAccountsSum';
  postgresQuery: CreditAgreement.Config.CommonPostgresQuery.Query.TAccountsSumQuery;
  startDate?: Util.FullDateStr;
  endDate?: Util.FullDateStr;
}

interface PreviewPostgresQueryOrgProvidedInputProps
  extends PreviewPostgresQueryButtonBaseProps {
  queryType: 'orgProvidedInput';
  postgresQuery: CreditAgreement.Config.CommonPostgresQuery.Query.OrgProvidedInputQuery;
  reportingDate?: Util.FullDateStr;
  reportingPeriod: Date.Reporting.ReportingPeriod;
  orgProvidedInputKey: string;
}

interface PreviewPostgresQueryPrincipalBalancePointInTimeProps
  extends PreviewPostgresQueryButtonBaseProps {
  queryType: 'principalBalancePointInTime';
  postgresQuery: CreditAgreement.Config.CommonPostgresQuery.Query.PrincipalBalancePointInTimeQuery;
  reportingDate?: Util.FullDateStr;
}

type PreviewPostgresQueryButtonProps =
  | PreviewPostgresQueryButtonPrincipalBalanceDailyAverageProps
  | PreviewPostgresQueryButtonTAccountsSumProps
  | PreviewPostgresQueryOrgProvidedInputProps
  | PreviewPostgresQueryPrincipalBalancePointInTimeProps;

export const PreviewPostgresQueryButton = (
  props: PreviewPostgresQueryButtonProps,
) => {
  const {
    wipConfig,
    postgresQuery,
    inputDateType,
    selectedInputDate,
    presetVariables,
    queryType,
  } = props;

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [queryResultsData, setQueryResultsData] = useState<
    PostgresQueryResultWithWarningsAndInputsGQLType | undefined
  >();

  const inputDate = useMemo(
    () => selectedInputDate ?? commonDate.currentDateStr(),
    [selectedInputDate],
  );

  const creditAgreementId = getIn(wipConfig.formProps.values, 'id');

  const supplementalVariables: CreditAgreement.Config.QueryVariable[] = [
    { name: inputDateType, value: inputDate, parseNumber: true },
  ];

  // If there's any overlap with provided "presetVariables", prioritize the
  // value that's set up with the modal through "supplementalVariables" and
  // filter out what already existed in "presetVariables"
  const updatedPresetVariables = presetVariables.filter((preset) => {
    return preset.name !== inputDateType;
  });

  const postgresQueryWithVariables = {
    ...postgresQuery,
    variables: [
      ...postgresQuery.variables,
      ...supplementalVariables,
      ...updatedPresetVariables,
    ],
  };

  const [
    evaluatePostgresQuery,
    { loading: isPostgresQueryDataLoading },
  ] = useEvaluatePortfolioCovenantPostgresQueryLazyQueryGQLType({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      creditAgreementId,
      postgresQuery: postgresQueryWithVariables,
    },
    onCompleted: ({ evaluatePortfolioCovenantPostgresQuery }) => {
      setQueryResultsData(evaluatePortfolioCovenantPostgresQuery);
      onOpen();
    },
  });

  const onClosePreviewDrawer = useCallback(() => {
    onClose();
    setQueryResultsData(undefined);
  }, [onClose, setQueryResultsData]);

  return (
    <Flex alignItems="center" width="100%" justifyContent="flex-end" mt="64px">
      <Button
        color={colors.aurora[400]}
        border="2px"
        borderRadius="8px"
        borderColor={colors.aurora[400]}
        backgroundColor="white"
        onClick={() => evaluatePostgresQuery()}
        isLoading={isPostgresQueryDataLoading}
      >
        Preview Query
      </Button>

      {queryType === 'principalBalanceDailyAverage' && (
        <LenderPostgresPreviewDrawer
          isOpen={isOpen}
          onClose={onClosePreviewDrawer}
          inputDate={inputDate}
          inputDateType={inputDateType}
          postgresQuery={postgresQuery}
          queryType={postgresQuery.type}
          // eslint-disable-next-line react/destructuring-assignment
          startDate={props.startDate}
          // eslint-disable-next-line react/destructuring-assignment
          endDate={props.endDate}
          queryData={queryResultsData}
        />
      )}

      {queryType === 'tAccountsSum' && (
        <LenderPostgresPreviewDrawer
          isOpen={isOpen}
          onClose={onClosePreviewDrawer}
          inputDate={inputDate}
          inputDateType={inputDateType}
          postgresQuery={postgresQuery}
          queryType={postgresQuery.type}
          // eslint-disable-next-line react/destructuring-assignment
          startDate={props.startDate}
          // eslint-disable-next-line react/destructuring-assignment
          endDate={props.endDate}
          queryData={queryResultsData}
        />
      )}

      {queryType === 'orgProvidedInput' && (
        <LenderPostgresPreviewDrawer
          isOpen={isOpen}
          onClose={onClosePreviewDrawer}
          inputDate={inputDate}
          inputDateType={inputDateType}
          postgresQuery={postgresQuery}
          queryType={postgresQuery.type}
          // eslint-disable-next-line react/destructuring-assignment
          reportingDate={props.reportingDate}
          // eslint-disable-next-line react/destructuring-assignment
          reportingPeriod={props.reportingPeriod}
          // eslint-disable-next-line react/destructuring-assignment
          orgProvidedInputKey={props.orgProvidedInputKey}
          queryData={queryResultsData}
        />
      )}

      {queryType === 'principalBalancePointInTime' && (
        <LenderPostgresPreviewDrawer
          isOpen={isOpen}
          onClose={onClosePreviewDrawer}
          inputDate={inputDate}
          inputDateType={inputDateType}
          postgresQuery={postgresQuery}
          queryType={postgresQuery.type}
          // eslint-disable-next-line react/destructuring-assignment
          reportingDate={props.reportingDate}
          queryData={queryResultsData}
        />
      )}
    </Flex>
  );
};
